// Copyright 2022 Carnegie Mellon University. All Rights Reserved.
// Released under a MIT (SEI)-style license, please see LICENSE.md in the
// project root for license information or contact permission@sei.cmu.edu for full terms.

import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import {
  ScoringModel,
  ItemStatus,
  RightSideDisplay,
  User,
} from 'src/app/generated/cite.api/model/models';
import { ScoringModelDataService } from 'src/app/data/scoring-model/scoring-model-data.service';
import { ScoringModelQuery } from 'src/app/data/scoring-model/scoring-model.query';
import { ComnSettingsService, CrucibleDialogService } from '@cmusei/crucible-common';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { AdminScoringModelEditDialogComponent } from '../admin-scoring-model-edit-dialog/admin-scoring-model-edit-dialog.component';
import { UserQuery } from 'src/app/data/user/user.query';
import { ActivatedRoute, Router } from '@angular/router';
import { PermissionDataService } from 'src/app/data/permission/permission-data.service';

@Component({
    selector: 'app-admin-scoring-models',
    templateUrl: './admin-scoring-models.component.html',
    styleUrls: ['./admin-scoring-models.component.scss'],
    standalone: false,
    animations: [
      trigger('detailExpand', [
        state('collapsed', style({ height: '0px', minHeight: '0' })),
        state('expanded', style({ height: '*' })),
        transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
      ]),
    ],
})
export class AdminScoringModelsComponent implements OnInit, OnDestroy {
  scoringModelList: ScoringModel[] = [];
  sortedScoringModelList: ScoringModel[] = [];
  userList: User[] = [];
  pageSize: number = 50;
  pageIndex: number = 0;
  filterControl = new UntypedFormControl();
  filterString = '';
  filteredScoringModelList: ScoringModel[] = [];
  newScoringModel: ScoringModel = { id: '', description: '' };
  isLoading = false;
  topbarColor = '#ef3a47';
  displayedScoringModels: ScoringModel[] = [];
  addingNewScoringModel = false;
  newScoringModelDescription = '';
  editScoringModel: ScoringModel = {};
  displayedColumns = ['actions', 'description', 'createdBy', 'dateCreated', 'status'];
  previewScoringModelId = '';
  scoringCategoryId = '';
  itemStatuses = [
    ItemStatus.Pending,
    ItemStatus.Active,
    ItemStatus.Cancelled,
    ItemStatus.Complete,
    ItemStatus.Archived
  ];
  selectedStatuses = [ ItemStatus.Pending, ItemStatus.Active ];
  rightSideDisplays = [
    RightSideDisplay.ScoreSummary,
    RightSideDisplay.HtmlBlock,
    RightSideDisplay.EmbeddedUrl,
    RightSideDisplay.Scoresheet,
    RightSideDisplay.None
  ];
  private unsubscribe$ = new Subject();
  sort: Sort = {active: 'description', direction: 'asc'};
  isBusy = false;
  uploadProgress = 0;
  @ViewChild('jsonInput') jsonInput: ElementRef<HTMLInputElement>;
  showAll = false;
  canCreateScoringModels = this.permissionDataService.canCreateScoringModels();

  constructor(
    private settingsService: ComnSettingsService,
    private scoringModelDataService: ScoringModelDataService,
    private scoringModelQuery: ScoringModelQuery,
    private userQuery: UserQuery,
    private dialog: MatDialog,
    public dialogService: CrucibleDialogService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private permissionDataService: PermissionDataService
  ) {
    this.scoringModelDataService.load();
    // observe the scoring models
    this.scoringModelQuery.selectAll().pipe(takeUntil(this.unsubscribe$)).subscribe(scoringModels => {
      this.scoringModelList = scoringModels;
    });
    // oberve the users
    this.userQuery.selectAll().pipe(takeUntil(this.unsubscribe$)).subscribe(users => {
      this.userList = users;
    });
    // subscribe to scoring models loading
    this.scoringModelQuery.selectLoading().pipe(takeUntil(this.unsubscribe$)).subscribe((isLoading) => {
      this.isBusy = isLoading;
    });

    this.filterControl.valueChanges
      .pipe(
        takeUntil(this.unsubscribe$)
      )
      .subscribe((term) => {
        this.filterString = term.trim().toLowerCase();
        this.applyFilter();
      });
    this.activatedRoute.queryParamMap
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((params) => {
        this.previewScoringModelId = params.get('scoringModelId');
      });
  }

  ngOnInit() {
    this.loadInitialData();
  }

  loadInitialData() {
    this.scoringModelQuery.selectAll().pipe(takeUntil(this.unsubscribe$)).subscribe(models => {
      this.scoringModelList = Array.from(models);
      this.applyFilter();
    })
  }

  addOrEditScoringModel(scoringModel: ScoringModel) {
    var hasPermission = false;
    if (!scoringModel) {
      scoringModel = {
        description: '',
        calculationEquation: '{average}',
        status: ItemStatus.Pending
      };
      hasPermission = this.permissionDataService.canCreateScoringModels();
    } else {
      scoringModel = {... scoringModel};
      hasPermission = this.permissionDataService.canEditScoringModel(scoringModel.id);
    }
    const dialogRef = this.dialog.open(AdminScoringModelEditDialogComponent, {
      maxWidth: '90vw',
      width: 'auto',
      minWidth: '900px',
      data: {
        scoringModel: scoringModel,
        itemStatuses: this.itemStatuses,
        rightSideDisplays: this.rightSideDisplays,
        canEdit: !scoringModel.evaluationId && hasPermission
      },
    });
    dialogRef.componentInstance.editComplete.subscribe((result) => {
      if (result.saveChanges && result.scoringModel) {
        this.saveScoringModel(result.scoringModel);
      }
      dialogRef.close();
    });
  }

  togglePanel(scoringModel: ScoringModel) {
    this.editScoringModel =
      this.editScoringModel.id === scoringModel.id ? this.editScoringModel = {} : this.editScoringModel = { ...scoringModel};
  }

  canEdit(id: string): boolean {
    return this.permissionDataService.canEditScoringModel(id);
  }

  canManage(id: string): boolean {
    return this.permissionDataService.canManageScoringModel(id);
  }

  saveScoringModel(scoringModel: ScoringModel) {
    if (scoringModel.id) {
      this.scoringModelDataService.updateScoringModel(scoringModel);
    } else {
      this.scoringModelDataService.add(scoringModel);
    }
  }

  deleteScoringModelRequest(scoringModel: ScoringModel) {
    this.dialogService
      .confirm({
        title: 'Delete this scoringModel?',
        message: 'Are you sure that you want to delete ' + scoringModel.description + '?',
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed) {
          this.scoringModelDataService.delete(scoringModel.id);
        }
      });
  }

  applyFilter() {
    this.filteredScoringModelList = this.scoringModelList.filter(model =>
        (!this.filterString ||
        model.description.toLowerCase().includes(this.filterString)) &&
        ((this.showAll) || (!model.evaluationId && !this.showAll)) &&
        this.selectedStatuses.some(status => status === model.status)
    );
    this.sortChanged(this.sort);
  }

  clearFilter() {
    this.filterControl.setValue('');
  }

  sortChanged(sort: Sort) {
    this.sort = sort;
    this.filteredScoringModelList.sort((a, b) => this.sortScoringModels(a, b, sort.active, sort.direction));
    this.applyPagination();
  }

  private sortScoringModels(
    a: ScoringModel,
    b: ScoringModel,
    column: string,
    direction: string
  ) {
    let returnValue = 0;
    const isAsc = direction !== 'desc';
    switch (column) {
      case 'dateCreated':
        returnValue = ( (a.dateCreated < b.dateCreated ? -1 : 1) * (isAsc ? 1 : -1) );
        break;
      case 'status':
        returnValue = ( (a.status < b.status ? -1 : 1) * (isAsc ? 1 : -1) );
        break;
      case 'createdBy':
        returnValue = ( (a.createdBy.toLowerCase() < b.createdBy.toLowerCase() ? -1 : 1) * (isAsc ? 1 : -1) );
        break;
      case 'description':
      default:
        returnValue = ( (a.description.toLowerCase() < b.description.toLowerCase() ? -1 : 1) * (isAsc ? 1 : -1) );
        break;
    }
    return returnValue;
  }

  getUserName(id: string) {
    const user = this.userList.find(u => u.id === id);
    return user ? user.name : '?';
  }

  copyScoringModel(id: string): void {
    this.scoringModelDataService.copy(id);
  }

  previewScoringModel(scoringModelId: string) {
    this.router.navigate([], {
      queryParams: {
        section: 'Scoring Models',
        scoringModelId: scoringModelId,
      },
    });
  }

  downloadScoringModel(scoringModel: ScoringModel) {
    this.isBusy = true;
    this.scoringModelDataService.downloadJson(scoringModel.id).subscribe(
      (data) => {
        const url = window.URL.createObjectURL(data);
        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.download = scoringModel.description + '-scoring-model.json';
        link.click();
        this.isBusy = false;
      },
      (err) => {
        this.isBusy = false;
        window.alert('Error downloading file');
      },
      () => {
        this.isBusy = false;
      }
    );
  }

  uploadFile(fileType: string, mselId: string, teamId: string) {
    this.isBusy = true;
  }

  /**
   * Selects the file(s) to be uploaded. Called when file selection is changed
   */
  selectFile(e) {
    const file = e.target.files[0];
    if (!file) {
      this.isBusy = false;
      return;
    }
    this.uploadProgress = 0;
    this.isBusy = true;
    this.scoringModelDataService.uploadJson(file, 'events', true);
    this.jsonInput.nativeElement.value = null;
  }

  paginatorEvent(page: PageEvent) {
    this.pageSize = page.pageSize;
    this.pageIndex = page.pageIndex;
    this.applyPagination();
  }

  applyPagination() {
    const startIndex = this.pageIndex * this.pageSize;
    this.displayedScoringModels = this.filteredScoringModelList.slice(startIndex, startIndex + this.pageSize);
  }

  trackById = (_: number, row: ScoringModel) => row.id;

  ngOnDestroy() {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }

}
